import React from 'react';
import { RsiTranslations } from './translations/RsiTranslations.types';
import { useRsiIntl } from './translations/useRsiIntl';
import { Icon, styled } from '@beeline.com/bp-design-system';
import { DeepPartial } from './types';
import merge from 'lodash/merge';

export const useRsiTranslations = (overrideTranslations: DeepPartial<RsiTranslations>) => {
  const intl = useRsiIntl();

  const defaultTranslations = {
    uploadStep: {
      title: intl.uploadAFile,
      manifestTitle: intl.addOrModifyRecords,
      maxRecordsExceeded: (maxRecords: number) => intl.tooManyRecords({ maxRecords }),
      dropzone: {
        title: intl.dragAndDropOrSelect({ selectAFile: <StyledSelectAFile>{intl.selectAFile}</StyledSelectAFile> }),
        subTitle: intl.supportedFormats,
        recommendedMaximumRows: intl.recommendedMaximumRows,
        errorToastDescription: intl.uploadRejected,
        activeDropzoneTitle: intl.dropFileHere,
        loadingTitle: intl.processing
      },
      selectSheet: {
        title: intl.selectTheSheetToUse,
        subTitle: intl.fileContainsMultipleSheets,
        nextButtonTitle: intl.next,
        backButtonTitle: intl.back
      }
    },
    selectHeaderStep: {
      title: intl.selectHeaderRow,
      subTitle: intl.selectRowContainingColumnHeaders,
      nextButtonTitle: intl.next,
      backButtonTitle: intl.back
    },
    matchColumnsStep: {
      title: intl.mapTheFields,
      nextButtonTitle: intl.next,
      backButtonTitle: intl.back,
      userTableTitle: intl.eachHeaderIsMappedReview,
      userTableSubTitle: intl.headers,
      templateTitle: intl.fieldsInProfessional,
      selectPlaceholder: intl.selectColumn,
      ignoredColumnText: intl.columnIgnored,
      subSelectPlaceholder: intl.select,
      matchDropdownTitle: intl.match,
      unmatched: intl.unmatched,
      duplicateColumnWarningTitle: intl.anotherColumnUnselected,
      duplicateColumnWarningDescription: intl.columnsCannotDuplicate
    },
    validationStep: {
      title: intl.verifyYourData,
      subTitle: intl.reviewYourInformationBeforeSubmitting,
      nextButtonTitle: intl.importData,
      backButtonTitle: intl.back,
      noRowsMessage: intl.noDataFound,
      noRowsMessageWhenFiltered: intl.noDataContainingErrors,
      discardButtonTitle: intl.discardSelectedRows,
      filterSwitchTitle: intl.showRowsWithWarnings,
      errorsOnlyCheckboxTitle: intl.showRowsWithErrors
    },
    alerts: {
      confirmClose: {
        headerTitle: intl.exitImportDataFlow,
        bodyText: intl.allChangesWillBeLostAreYouSure,
        cancelButtonTitle: intl.cancel,
        exitButtonTitle: intl.exit
      },
      confirmBack: {
        title: intl.goBackToThePreviousStep,
        subTitle: intl.allChangesWillBeLost,
        goBack: intl.goBack
      },
      confirmation: {
        title: (entityType: string) => intl.yourImportWasSubmitted({ entityType }),
        subTitle: intl.checkNotificationBellAreaForDetails({
          notifications: <b>{intl.notifications}</b>,
          bell: <Icon name="notifications" />
        }),
        importData: intl.importMoreData,
        backToScreen: (entityType: string, hasOnlyOneImportType: boolean) =>
          intl.backToScreen({ screen: hasOnlyOneImportType ? entityType : intl.home })
      },
      submitIncomplete: {
        headerTitle: intl.errorsDetected,
        bodyText: intl.someRowsContainErrorsRowWillBeIgnored,
        bodyTextSubmitForbidden: intl.stillSomeRowsContainingErrors,
        cancelButtonTitle: intl.cancel,
        finishButtonTitle: intl.submit
      },
      submitError: {
        title: intl.error,
        defaultMessage: intl.errorOccurredWhileSubmittingData
      },
      unmatchedRequiredFields: {
        headerTitle: intl.notAllColumnsMatched,
        bodyText: intl.requiredColumnsNotMatchedContinue,
        listTitle: intl.columnsNotMatched,
        cancelButtonTitle: intl.cancel,
        continueButtonTitle: intl.continue
      },
      toast: {
        error: intl.error
      }
    }
  };

  return merge(defaultTranslations, overrideTranslations);
};

const StyledSelectAFile = styled('span')`
  color: ${({ theme }) => theme.palette.primary.main};
`;
